import type {
  CreateMarketingCampaignPayload,
  MarketingCampaign,
  MarketingCampaignSize,
  MarketingCampaignStatus,
  MarketingChannel,
  OfferOverride,
  OfferOverrideExclusion,
  OfferOverrideInput,
  ProposeMarketingCampaignPayload,
  UpdateMarketingCampaignPayload,
} from "../schemas";
import type { SelectAudienceOption } from "@/features/BroadcastV2/components/SelectAudienceField";
import type { AudiencesBroadcast } from "@/features/BroadcastV2/types";
import type { Frame } from "@/types/dragon-tiles";
import type { Dayjs } from "dayjs";

import dayjs from "@/utils/configuredDayjs";

/**
 * A planned send: a snapshot of a REAL BroadcastV2 draft broadcast created
 * during planning. Editing happens on the real broadcast edit pages; the
 * snapshot keeps the sidebar/review/calendar renderable and is refreshed
 * from the broadcasts API when the wizard regains focus.
 */
export interface PlannedBroadcast {
  broadcastId: string;
  name: string;
  channel: MarketingChannel;
  /** ISO 8601 datetime, UTC. Null when sendInstantly. */
  sendAt: string | null;
  sendInstantly: boolean;
}

export interface PlanEventDraft {
  name: string;
  /** Free-form; the UI offers a suggested list plus custom entries. */
  eventType: string | null;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  /** Site-local "HH:MM"; null = start of day / through end of day. */
  startTime: string | null;
  endTime: string | null;
  offerSlug: string | null;
  promoCode: string | null;
  promoDescription: string | null;
  /** Announcement bar copy + click-through (storage only, see FOG-2281). */
  websiteTopBarContent: string | null;
  websiteTopBarUrl: string | null;
  /** Campaign offer override (FOG-2459); null = off. Broadcasts are the
   * "broadcast" tile slug, matching the backend's exclusion contract. */
  offerOverride: PlanOfferOverride | null;
  /** Default audiences, applied to newly created broadcasts. */
  audiences: SelectAudienceOption[];
  broadcasts: PlannedBroadcast[];
}

export interface PlanOfferOverride {
  excludedTileSlugs: string[];
  excludedExchangeIds: string[];
  /** Stored fields this UI doesn't edit, written back untouched. */
  carried?: Pick<OfferOverrideInput, "window_start" | "window_end"> & {
    exclusions: OfferOverrideExclusion[];
  };
}

/** The tile slug the backend uses for broadcasts in exclusions. */
export const BROADCAST_TILE_SLUG = "broadcast";

export const emptyPlanEventDraft: PlanEventDraft = {
  name: "",
  eventType: null,
  startDate: null,
  endDate: null,
  startTime: null,
  endTime: null,
  offerSlug: null,
  promoCode: null,
  promoDescription: null,
  websiteTopBarContent: null,
  websiteTopBarUrl: null,
  offerOverride: null,
  audiences: [],
  broadcasts: [],
};

/** Calendar-select prefill: "YYYY-MM-DD" search params → draft dates. An
 * invalid or missing part is dropped so a hand-edited URL degrades to an
 * empty field instead of an Invalid Date in the picker. Past days clamp to
 * today — new events can't start in the past, so a click/drag on history
 * (or a hand-edited URL) prefills the earliest plannable day instead. */
export function draftDatesFromSearch(
  start?: string,
  end?: string,
): Partial<PlanEventDraft> {
  const today = dayjs().startOf("day");
  const parse = (value?: string) => {
    const date = value ? dayjs(value, "YYYY-MM-DD", true) : null;
    if (!date?.isValid()) return null;
    return date.isBefore(today, "day") ? today : date;
  };
  const startDate = parse(start);
  const endDate = parse(end);
  return {
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  };
}

/** Channels the picked tile can actually send on. */
export function frameChannels(frame: Frame): MarketingChannel[] {
  const mediums = new Set(
    frame.frame_variants.map((variant) => variant.medium),
  );
  return ["email", "sms", "mms", "rcs"].filter((channel) =>
    mediums.has(channel as MarketingChannel),
  ) as MarketingChannel[];
}

/** Snapshot a real audiences-broadcast for the plan (sidebar/calendar). */
export function plannedBroadcastFromAudiences(
  broadcast: AudiencesBroadcast,
  fallbackChannel: MarketingChannel,
): PlannedBroadcast {
  const schedule = broadcast.schedule;
  const sendAt =
    schedule != null && "scheduledAt" in schedule ? schedule.scheduledAt : null;
  const channel =
    broadcast.forceChannel === "MEDIUM_TYPE_EMAIL"
      ? "email"
      : broadcast.forceChannel === "MEDIUM_TYPE_SMS"
        ? "sms"
        : fallbackChannel;
  return {
    broadcastId: broadcast.id,
    name: broadcast.name,
    channel,
    sendAt,
    sendInstantly: sendAt === null,
  };
}

/** Scheduled sends must land inside the campaign's date span; null = fine. */
export function broadcastScheduleError(
  broadcast: PlannedBroadcast,
  draft: PlanEventDraft,
): string | null {
  if (broadcast.sendInstantly || !broadcast.sendAt) return null;
  if (!draft.startDate || !draft.endDate) return null;
  const sendAt = dayjs(broadcast.sendAt);
  const inRange =
    !sendAt.isBefore(draft.startDate, "day") &&
    !sendAt.isAfter(draft.endDate, "day");
  return inRange
    ? null
    : `${broadcast.name} is scheduled outside the campaign dates (${draft.startDate.format(
        "MMM D",
      )} to ${draft.endDate.format("MMM D")}).`;
}

export function broadcastSendLabel(broadcast: PlannedBroadcast): {
  date: string;
  time: string;
} {
  if (broadcast.sendInstantly || !broadcast.sendAt) {
    return { date: "Send Instantly", time: "" };
  }
  const sendAt = dayjs(broadcast.sendAt);
  return {
    date: sendAt.format("MMM D, YYYY"),
    time: sendAt.format("h:mma"),
  };
}

/** Campaign date span constraining plan-created broadcasts ("YYYY-MM-DD"),
 * or null while the wizard's dates are unset. The composer disables Send
 * Instantly and clamps the schedule picker to this range. */
export function planScheduleWindow(
  draft: PlanEventDraft,
): { startDate: string; endDate: string } | null {
  if (!draft.startDate || !draft.endDate) return null;
  return {
    startDate: draft.startDate.format("YYYY-MM-DD"),
    endDate: draft.endDate.format("YYYY-MM-DD"),
  };
}

/** Where a planned broadcast is edited: the real BroadcastV2 editor.
 * Inferred literal-union return, so router Links keep their typing. */
export function broadcastEditRoute(
  broadcast: Pick<PlannedBroadcast, "channel">,
) {
  return broadcast.channel === "email"
    ? "/dragon-tiles/$siteId/broadcastV2/$broadcastId/email"
    : "/dragon-tiles/$siteId/broadcastV2/$broadcastId/sms";
}

/** Whether a calendar click/drag span may start a new plan. Past days are
 * off-limits: new events can't start in the past (FOG-2393), matching the
 * details-step validation below. */
export function isSelectablePlanSpan(spanStart: Date): boolean {
  return !dayjs(spanStart).isBefore(dayjs().startOf("day"), "day");
}

/** Optional announcement link: empty is fine, anything else must be a full
 * https URL with a real hostname (the bar publishes to the storefront). */
export function announcementUrlError(url: string | null): string | null {
  const value = url?.trim();
  if (!value) return null;
  if (!value.startsWith("https://")) {
    return "Enter a full https:// link, e.g. https://yourstore.com/sale";
  }
  try {
    if (new URL(value).hostname.includes(".")) return null;
  } catch {
    // Unparseable: falls through to the error below.
  }
  return "Enter a valid link, e.g. https://yourstore.com/sale";
}

export interface DetailsFieldErrors {
  name?: string;
  eventType?: string;
  startDate?: string;
  endDate?: string;
  endTime?: string;
  offerSlug?: string;
  websiteTopBarUrl?: string;
}

/** Step-1 field prompts (FOG-2537): the exits stay enabled and surface these
 * on click. Includes the details-step-only Event Type rule (FOG-2364), which
 * isDetailsStepValid skips; an empty result means the step is passable. */
export function detailsStepErrors(
  draft: PlanEventDraft,
  editing = false,
): DetailsFieldErrors {
  const errors: DetailsFieldErrors = {};
  if (!draft.name.trim()) errors.name = "Enter a campaign name.";
  if (!draft.eventType?.trim()) {
    errors.eventType = "Select or add an event type.";
  }
  if (!draft.startDate) {
    errors.startDate = "Pick a start date.";
    // New events can't start in the past (FOG-2393); edits skip the rule.
  } else if (!editing && draft.startDate.isBefore(dayjs(), "day")) {
    errors.startDate = "Start date can't be in the past.";
  }
  if (!draft.endDate) {
    errors.endDate = "Pick an end date.";
  } else if (
    draft.startDate &&
    draft.endDate.isBefore(draft.startDate, "day")
  ) {
    errors.endDate = "End date can't be before the start date.";
  } else if (
    draft.startDate &&
    draft.endDate.isSame(draft.startDate, "day") &&
    draft.endTime &&
    // Zero-padded "HH:MM" compares correctly as a string; blank start = 00:00.
    draft.endTime <= (draft.startTime ?? "00:00")
  ) {
    errors.endTime = "End time must be after the start time.";
  }
  if (draft.offerOverride && !draft.offerSlug) {
    errors.offerSlug = "Pick an offer to use everywhere.";
  }
  const urlError = announcementUrlError(draft.websiteTopBarUrl);
  if (urlError) errors.websiteTopBarUrl = urlError;
  return errors;
}

/** Same rules as detailsStepErrors minus Event Type, which review-page
 * consumers skip (FOG-2364): older campaigns may store a null event_type. */
export function isDetailsStepValid(
  draft: PlanEventDraft,
  editing = false,
): boolean {
  return Object.keys(detailsStepErrors(draft, editing)).every(
    (key) => key === "eventType",
  );
}

/** Step-1 draft as a propose payload (FOG-2521): set fields constrain
 * Blastro's planning, blank ones are omitted so Blastro generates them.
 * A null size is omitted too: Blastro then upsizes from Small (FOG-2586). */
export function toProposeCampaignPayload(
  draft: PlanEventDraft,
  campaignSize: MarketingCampaignSize | null,
): ProposeMarketingCampaignPayload {
  const name = draft.name.trim();
  const eventType = draft.eventType?.trim();
  const content = draft.websiteTopBarContent?.trim();
  const url = draft.websiteTopBarUrl?.trim();
  return {
    ...(name ? { name } : {}),
    ...(draft.audiences.length > 0
      ? { audience_ids: draft.audiences.map((audience) => audience.id) }
      : {}),
    ...(eventType ? { event_type: eventType } : {}),
    ...(draft.startDate
      ? { start_date: draft.startDate.format("YYYY-MM-DD") }
      : {}),
    ...(draft.endDate ? { end_date: draft.endDate.format("YYYY-MM-DD") } : {}),
    ...(content ? { website_top_bar_content: content } : {}),
    ...(url ? { website_top_bar_url: url } : {}),
    ...(campaignSize ? { campaign_size: campaignSize } : {}),
  };
}

/** Announcement copy caps at 80 chars for generated campaigns. Dates are
 * deliberately NOT pre-checked: Blastro owns its own planning horizon, so
 * far-future briefs go through rather than being blocked in the UI. */
export function proposeValidationError(draft: PlanEventDraft): string | null {
  if ((draft.websiteTopBarContent?.trim().length ?? 0) > 80) {
    return "Announcement text must be 80 characters or fewer for generated campaigns.";
  }
  return null;
}

/** All planned sends must sit inside the campaign's date span. */
export function isBroadcastsStepValid(draft: PlanEventDraft): boolean {
  return draft.broadcasts.every(
    (broadcast) => broadcastScheduleError(broadcast, draft) === null,
  );
}

/** Existing campaign → editable draft. Stored audiences hydrate (FOG-2419):
 * they seed broadcasts added from edit and are PATCHable on the details step.
 * Existing broadcasts still own their own audiences, untouched by this. */
export function draftFromCampaign(
  campaign: MarketingCampaign,
  // Unflagged, the times and override stay out of the draft as they stay out
  // of the payload: their hidden fields can't block Save there.
  { offerOverride }: CampaignPayloadOptions = NO_OFFER_OVERRIDE,
): PlanEventDraft {
  const override = offerOverride ? campaign.offer_override : null;
  return {
    name: campaign.name,
    eventType: campaign.event_type,
    startDate: dayjs(campaign.start_date),
    endDate: dayjs(campaign.end_date),
    startTime: (offerOverride && campaign.start_time) || null,
    endTime: (offerOverride && campaign.end_time) || null,
    offerSlug: campaign.offer_slug,
    promoCode: campaign.promo_code,
    promoDescription: campaign.promo_description,
    websiteTopBarContent: campaign.website_top_bar_content,
    websiteTopBarUrl: campaign.website_top_bar_url,
    offerOverride: override
      ? {
          excludedTileSlugs: excludedMatches(override, "dragon_tile"),
          excludedExchangeIds: excludedMatches(override, "exchange"),
          carried: {
            window_start: override.window_start,
            window_end: override.window_end,
            exclusions: override.exclusions.filter(
              (exclusion) => !EDITABLE_KINDS.includes(exclusion.kind),
            ),
          },
        }
      : null,
    // The backend resolves audience_ids to id+name refs on every read.
    audiences: campaign.audiences.map((audience) => ({
      id: audience.id,
      name: audience.name,
      key: `audience:${audience.id}`,
    })),
    broadcasts: campaign.broadcasts.map((broadcast) => ({
      broadcastId: broadcast.broadcast_id,
      name: broadcast.name,
      channel: broadcast.channel,
      sendAt: broadcast.send_at,
      sendInstantly: broadcast.send_instantly,
    })),
  };
}

/** Exclusion kinds this UI edits; any other kind is carried through as-is. */
const EDITABLE_KINDS = ["dragon_tile", "exchange"];

function excludedMatches(
  override: OfferOverride,
  kind: "dragon_tile" | "exchange",
): string[] {
  return override.exclusions
    .filter((exclusion) => exclusion.kind === kind)
    .map((exclusion) => exclusion.match);
}

/** Draft override → wire block. A new override leaves the window unset so it
 * inherits the campaign's dates + times in the site timezone; an edited one
 * keeps the window and exclusions it was stored with, since a PATCH replaces
 * the whole block. */
function toOfferOverride(
  override: PlanOfferOverride | null,
): OfferOverrideInput | null {
  if (!override) return null;
  const exclude = (kind: string, match: string) => ({
    kind,
    match,
    action: "OFFER_OVERRIDE_EXCLUSION_ACTION_EXCLUDE" as const,
  });
  const { exclusions: carriedExclusions = [], ...window } =
    override.carried ?? {};
  return {
    ...window,
    behavior: "OFFER_OVERRIDE_BEHAVIOR_SWAP_CODE",
    exclusions: [
      ...override.excludedTileSlugs.map((slug) => exclude("dragon_tile", slug)),
      ...override.excludedExchangeIds.map((id) => exclude("exchange", id)),
      ...carriedExclusions,
    ],
  };
}

export interface CampaignPayloadOptions {
  /** Site is on the offer override allowlist. Off = the times and override
   * keys stay out of the body, so a PATCH leaves stored values untouched. */
  offerOverride: boolean;
}

const NO_OFFER_OVERRIDE: CampaignPayloadOptions = { offerOverride: false };

/** The campaign fields shared by POST and PATCH. Status is create-only (the
 * backend ignores it on PATCH); audience_ids travel on both now that edits
 * hydrate them (FOG-2419). Throws if step validity was bypassed. */
function toCampaignFields(
  draft: PlanEventDraft,
  { offerOverride }: CampaignPayloadOptions,
): Omit<CreateMarketingCampaignPayload, "status"> {
  if (!draft.startDate || !draft.endDate) {
    throw new Error("campaign payload requires start and end dates");
  }

  return {
    name: draft.name.trim(),
    audience_ids: draft.audiences.map((audience) => audience.id),
    event_type: draft.eventType?.trim() || null,
    start_date: draft.startDate.format("YYYY-MM-DD"),
    end_date: draft.endDate.format("YYYY-MM-DD"),
    offer_slug: draft.offerSlug,
    promo_code: draft.promoCode,
    promo_description: draft.promoDescription?.trim() || null,
    website_top_bar_content: draft.websiteTopBarContent?.trim() || null,
    website_top_bar_url: draft.websiteTopBarUrl?.trim() || null,
    broadcasts: toBroadcastRefs(draft.broadcasts),
    ...(offerOverride
      ? {
          start_time: draft.startTime,
          end_time: draft.endTime,
          offer_override: toOfferOverride(draft.offerOverride),
        }
      : {}),
  };
}

/** Planned broadcasts → id-only wire refs, for full payloads and
 * broadcasts-only PATCHes. Display fields are read-only (CON-186). */
export function toBroadcastRefs(
  broadcasts: PlannedBroadcast[],
): CreateMarketingCampaignPayload["broadcasts"] {
  return broadcasts.map((broadcast) => ({
    broadcast_id: broadcast.broadcastId,
  }));
}

/** Draft → PATCH payload. Status never travels here: the backend ignores it
 * on PATCH — transitions go through the :schedule / :unschedule endpoints. */
export function toUpdateCampaignPayload(
  draft: PlanEventDraft,
  options: CampaignPayloadOptions = NO_OFFER_OVERRIDE,
): UpdateMarketingCampaignPayload {
  return toCampaignFields(draft, options);
}

/** Draft → POST payload. Creating with "scheduled" fans out atomically on
 * the backend (broadcasts activate with the create); after creation, status only
 * moves via the :schedule / :unschedule endpoints. */
export function toCreateCampaignPayload(
  draft: PlanEventDraft,
  status: MarketingCampaignStatus = "draft",
  options: CampaignPayloadOptions = NO_OFFER_OVERRIDE,
): CreateMarketingCampaignPayload {
  return {
    ...toCampaignFields(draft, options),
    status,
  };
}
